import { Component, Input, Output, EventEmitter, signal, OnChanges, SimpleChanges, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '../../directives/TranslatePipe';
import { HtmlEditorComponent } from '../html-editor/html-editor.component';
import { LookupItem } from '../../../services/LookupService';
import { IStaticStylesConfig } from '../../../skins/_primitives/IStaticStylesConfig';
import { Policy } from '../../../models/matwins-tenant/Policy.model';
import { Translation, TranslationRelationType } from '../../../models/matwins-tenant/navigation-entities/Translation.model';
import {
    TranslatableEntityConfig,
    TranslatableItem,
    TranslationFieldDef,
    TranslatableModalSaveEvent,
    ChildEntityConfig
} from './translatable-entity.interface';

interface PendingRdlcFile {
    id: number;
    file: File;
}

@Component({
    selector: 'app-translatable-entity-modal',
    standalone: true,
    imports: [CommonModule, FormsModule, TranslatePipe, HtmlEditorComponent],
    templateUrl: './translatable-entity-modal.component.html'
})
export class TranslatableEntityModalComponent implements OnChanges {

    @Input() config!: TranslatableEntityConfig;
    @Input() visible = false;
    @Input() languages: LookupItem[] = [];
    @Input() isEditMode = false;
    @Input() Skin!: IStaticStylesConfig;

    @Input() items: TranslatableItem[] = [];
    @Input() defaultLanguageId: number | string = 0;

    @Output() close = new EventEmitter<void>();
    @Output() saveAll = new EventEmitter<TranslatableModalSaveEvent>();
    @Output() deleteItem = new EventEmitter<number>();

    activeTab = signal<string>('general');
    editableItems = signal<TranslatableItem[]>([]);
    translationMap = signal<Map<string, string>>(new Map());
    childRightResponseMap = signal<Map<number, boolean>>(new Map());

    // --- Child entity state ---
    expandedItems = signal<Set<number>>(new Set());

    // Computed: total columns for colspan in child rows
    totalCols = computed(() => {
        let count = (this.config.singleItem ? 0 : 1) + (this.config.hideDescription ? 0 : 1);
        if (this.config.extraFields) count += this.config.extraFields.length;
        if (this.getDefaultLanguage()) {
            count += this.config.translationFields.length;
        }
        return count;
    });

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && this.visible) {
            this.initFromItems();
            this.activeTab.set('general');
        }
    }

    private initFromItems(): void {
        const cloned = this.items.map(item => ({
            ...item,
            translations: item.translations ? [...item.translations] : [],
            children: item.children ? item.children.map(c => ({
                ...c,
                translations: c.translations ? [...c.translations] : []
            })) : []
        }));
        // Un item unique est toujours présent (ex: l'alerte d'évacuation du site)
        if (this.config.singleItem && cloned.length === 0) {
            cloned.push({ id: 0, description: '', numero: 1, translations: [], children: [] });
        }
        this.editableItems.set(cloned);

        const tMap = new Map<string, string>();
        const rrMap = new Map<number, boolean>();

        for (const item of cloned) {
            for (const t of (item.translations || [])) {
                const key = this.buildKey(item.id, t.fK_Language, t.typeDeTraduction);
                tMap.set(key, t.translationValue || '');
            }
            for (const child of (item.children || [])) {
                if (child.isRightResponse != null) rrMap.set(child.id, child.isRightResponse);
                for (const t of (child.translations || [])) {
                    const key = this.buildChildKey(item.id, child.id, t.fK_Language, t.typeDeTraduction);
                    tMap.set(key, t.translationValue || '');
                }
            }
        }

        this.translationMap.set(tMap);
        this.childRightResponseMap.set(rrMap);
        this.expandedItems.set(new Set());
    }

    // --- Key builders ---
    private buildKey(itemId: number, langId: number, type: number): string {
        return `${itemId}-${langId}-${type}`;
    }

    private buildChildKey(parentId: number, childId: number, langId: number, type: number): string {
        return `p${parentId}_c${childId}-${langId}-${type}`;
    }

    // --- Parent translation getters/setters ---
    getTranslation(itemId: number, langId: number | string, type: number): string {
        return this.translationMap().get(this.buildKey(itemId, +langId, type)) || '';
    }

    setTranslation(itemId: number, langId: number | string, type: number, value: string): void {
        this.translationMap.update(m => {
            const copy = new Map(m);
            copy.set(this.buildKey(itemId, +langId, type), value);
            return copy;
        });
    }

    getChildRightResponse(childId: number): boolean {
        return this.childRightResponseMap().get(childId) || false;
    }

    setChildRightResponse(childId: number, value: boolean): void {
        this.childRightResponseMap.update(m => {
            const copy = new Map(m);
            copy.set(childId, value);
            return copy;
        });
    }

    // --- Child translation getters/setters ---
    getChildTranslation(parentId: number, childId: number, langId: number | string, type: number): string {
        return this.translationMap().get(this.buildChildKey(parentId, childId, +langId, type)) || '';
    }

    setChildTranslation(parentId: number, childId: number, langId: number | string, type: number, value: string): void {
        this.translationMap.update(m => {
            const copy = new Map(m);
            copy.set(this.buildChildKey(parentId, childId, +langId, type), value);
            return copy;
        });
    }


    // --- Expand / collapse ---
    toggleExpand(itemId: number): void {
        this.expandedItems.update(s => {
            const copy = new Set(s);
            if (copy.has(itemId)) copy.delete(itemId);
            else copy.add(itemId);
            return copy;
        });
    }

    isExpanded(itemId: number): boolean {
        return this.expandedItems().has(itemId);
    }

    // --- Language helpers ---
    getDefaultLanguage(): LookupItem | undefined {
        return this.languages.find(l => +l.id === +this.defaultLanguageId);
    }

    getOtherLanguages(): LookupItem[] {
        return this.languages.filter(l => +l.id !== +this.defaultLanguageId);
    }

    // --- Parent Item CRUD ---
    addItem(): void {
        const tempId = -(Date.now());
        const newItem: TranslatableItem = {
            id: tempId, description: '', numero: (this.editableItems().length + 1), translations: [],
            children: []
        };
        if (this.config.extraFields) {
            for (const field of this.config.extraFields) {
                newItem[field.property] = field.defaultValue ?? null;
            }
        }
        this.editableItems.update(items => [...items, newItem]);
    }

    removeItem(itemId: number): void {
        if (itemId > 0) {
            this.deleteItem.emit(itemId);
        }
        const removedItem = this.editableItems().find(i => i.id === itemId);

        this.editableItems.update(items => items.filter(i => i.id !== itemId));
        // Clean parent translations
        this.translationMap.update(m => {
            const copy = new Map(m);
            for (const key of copy.keys()) {
                if (key.startsWith(`${itemId}-`)) copy.delete(key);
            }
            return copy;
        });
        this.childRightResponseMap.update(m => {
            const copy = new Map(m);
            for (const child of (removedItem?.children || [])) {
                copy.delete(child.id);
            }
            return copy;
        });
        // Clean child translations
        this.translationMap.update(m => {
            const copy = new Map(m);
            for (const key of copy.keys()) {
                if (key.startsWith(`p${itemId}_`)) copy.delete(key);
            }
            return copy;
        });

        // Remove from expanded
        this.expandedItems.update(s => {
            const copy = new Set(s);
            copy.delete(itemId);
            return copy;
        });
    }

    updateItemDescription(itemId: number, value: string): void {
        this.editableItems.update(items =>
            items.map(i => i.id === itemId ? { ...i, description: value } : i)
        );
    }

    updateItemExtra(itemId: number, property: string, value: any): void {
        this.editableItems.update(items =>
            items.map(i => i.id === itemId ? { ...i, [property]: value } : i)
        );
    }

    // --- Child Item CRUD ---
    addChildItem(parentId: number): void {
        const tempId = -(Date.now() + Math.floor(Math.random() * 10000));
        const newChild: TranslatableItem = {
            id: tempId,
            description: '',
            numero: undefined,
            translations: []
        };
        this.editableItems.update(items =>
            items.map(i => {
                if (i.id === parentId) {
                    const children = [...(i.children || [])];
                    children.push(newChild);
                    return { ...i, children };
                }
                return i;
            })
        );
        // Auto-expand parent
        this.expandedItems.update(s => {
            const copy = new Set(s);
            copy.add(parentId);
            return copy;
        });
    }

    removeChildItem(parentId: number, childId: number): void {
        this.editableItems.update(items =>
            items.map(i => {
                if (i.id === parentId) {
                    return {
                        ...i,
                        children: (i.children || []).filter(c => c.id !== childId)
                    };
                }
                return i;
            })
        );
        // Clean child translations
        this.translationMap.update(m => {
            const copy = new Map(m);
            for (const key of copy.keys()) {
                if (key.startsWith(`p${parentId}_c${childId}-`)) copy.delete(key);
            }
            return copy;
        });
        this.childRightResponseMap.update(m => {
            const copy = new Map(m);
            copy.delete(childId);
            return copy;
        });
    }

    updateChildDescription(parentId: number, childId: number, value: string): void {
        this.editableItems.update(items =>
            items.map(i => {
                if (i.id === parentId) {
                    return {
                        ...i,
                        children: (i.children || []).map(c =>
                            c.id === childId ? { ...c, description: value } : c
                        )
                    };
                }
                return i;
            })
        );
    }

    // --- Save ---
    onSave(): void {
        const items = this.editableItems();
        const tMap = this.translationMap();
        const rrMap = this.childRightResponseMap();
        const translations: Translation[] = [];

        for (const item of items) {
            // Parent translations
            for (const lang of this.languages) {
                for (const field of this.config.translationFields) {
                    const key = this.buildKey(item.id, +lang.id, field.type);
                    const value = tMap.get(key);
                    if (value != null && value.trim() !== '') {
                        const t = new Translation();
                        t.id = Translation.buildId(this.config.relationType as any, item.id);
                        t.compoosed_Id = t.id;
                        t.fK_Language = +lang.id;
                        t.typeDeTraduction = field.type;
                        t.translationValue = value;
                        translations.push(t);
                    }
                }
            }

            // Child translations + set isRightResponse on child items
            if (this.config.childConfig) {
                for (const child of (item.children || [])) {
                    child.isRightResponse = rrMap.get(child.id) ?? false;
                    for (const lang of this.languages) {
                        for (const field of this.config.childConfig.translationFields) {
                            const key = this.buildChildKey(item.id, child.id, +lang.id, field.type);
                            const value = tMap.get(key);
                            if (value != null && value.trim() !== '') {
                                const t = new Translation();
                                t.id = Translation.buildId(
                                    TranslationRelationType.QuizResponse as any,
                                    child.id
                                );
                                t.compoosed_Id = t.id;
                                t.fK_Language = +lang.id;
                                t.typeDeTraduction = field.type;
                                t.translationValue = value;
                                translations.push(t);
                            }
                        }
                    }
                }
            }
        }

        this.saveAll.emit({ items, translations });
    }

    onClose(): void {
        this.close.emit();
    }

    trackById(index: number, item: TranslatableItem): number {
        return item.id;
    }

    trackByFieldType(index: number, field: TranslationFieldDef): number {
        return field.type;
    }

    trackByChildId(index: number, child: TranslatableItem): number {
        return child.id;
    }
}